import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { API_BASE } from '../apiConfig';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faNewspaper, faLightbulb, faTags, faChartLine, faEyeSlash,
    faPlus, faEdit, faTrash, faSearch, faImage, faBolt, faCheckCircle
} from '@fortawesome/free-solid-svg-icons';

const AdminBlog = () => {
    const [blogs, setBlogs] = useState([]);
    const [title, setTitle] = useState('');
    const [slug, setSlug] = useState('');
    const [category, setCategory] = useState('Օգտակար խորհուրդներ');
    const [image, setImage] = useState(null);
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(false);
    const [editId, setEditId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('Բոլորը');

    // --- GET DATA ---
    const fetchBlogs = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/api/blogs`, {
                headers: { 'ngrok-skip-browser-warning': 'true' }
            });
            const data = await res.json();
            setBlogs(Array.isArray(data) ? data : []);
        } catch (err) { console.error("Բեռնման սխալ:", err); }
    }, []);

    useEffect(() => { fetchBlogs(); }, [fetchBlogs]);

    // --- ANALYTICS LOGIC ---
    const stats = useMemo(() => ({
        total: blogs.length,
        useful: blogs.filter(b => b.category === 'Օգտակար խորհուրդներ').length,
        news: blogs.filter(b => b.category === 'Նորություններ').length,
        offers: blogs.filter(b => b.category === 'Հատուկ առաջարկներ').length,
        interesting: blogs.filter(b => b.category === 'Հետաքրքիր').length,
    }), [blogs]);

    const filteredBlogs = blogs.filter(blog => {
        const matchesSearch = blog.title.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = filterCategory === 'Բոլորը' || blog.category === filterCategory;
        return matchesSearch && matchesCategory;
    });

    // --- RESET FORM ---
    const resetForm = () => { 
        setEditId(null); setTitle(''); setSlug(''); setContent(''); setImage(null); 
    };

    // --- SUBMIT (CREATE / UPDATE) ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const token = localStorage.getItem('token');

        const plainText = content.replace(/<[^>]*>/g, '');
        const autoDescription = plainText.substring(0, 160) + "...";

        const formData = new FormData();
        formData.append('title', title);
        formData.append('slug', slug ? slug.toLowerCase().trim().replace(/\s+/g, '-') : title.toLowerCase().trim().replace(/\s+/g, '-'));
        formData.append('category', category);
        formData.append('description', autoDescription);
        formData.append('content', content);
        if (image) formData.append('image', image);

        const url = editId ? `${API_BASE}/api/blogs/${editId}` : `${API_BASE}/api/blogs`;
        
        try {
            const res = await fetch(url, {
                method: editId ? 'PUT' : 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            if(res.ok) {
                alert(editId ? "Փոփոխությունները պահպանված են" : "Հոդվածը հրապարակված է");
                resetForm();
                fetchBlogs();
            } else {
                alert("Սխալ սերվերի կողմից");
            }
        } catch (err) { alert("Կապի սխալ"); }
        setLoading(false);
    };

    // --- DELETE ---
    const handleDelete = async (id) => {
        if (!window.confirm("Վստա՞հ եք, որ ուզում եք ջնջել:")) return;
        const token = localStorage.getItem('token');
        try {
            const res = await fetch(`${API_BASE}/api/blogs/${id}`, {
                method: 'DELETE',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'ngrok-skip-browser-warning': 'true' 
                }
            });
            if (res.ok) {
                setBlogs(prev => prev.filter(blog => blog.id !== id));
                alert("Հաջողությամբ ջնջվեց");
            } else {
                alert("Չհաջողվեց ջնջել");
            }
        } catch (err) { alert("Սերվերի հետ կապի սխալ"); }
    };

    return (
        <div className="admin-luxury-container">
            <style>{luxuryStyles}</style>
            
            <header className="premium-header">
                <div className="header-brand">
                    <div className="bolt-icon"><FontAwesomeIcon icon={faBolt} /></div>
                    <div>
                        <h1>Բլոգի Կառավարում <span>v3.0</span></h1>
                        <p>Լիբեր Լոգիստիկա • Ադմին Պանել</p>
                    </div>
                </div>
            </header>

            {/* Analytics Grid */}
            <div className="analytics-grid">
                <div className="stat-glass-card blue">
                    <div className="stat-icon"><FontAwesomeIcon icon={faChartLine} /></div>
                    <div className="stat-info"><h4>{stats.total}</h4><p>Ընդհանուր</p></div>
                </div>
                <div className="stat-glass-card gold">
                    <div className="stat-icon"><FontAwesomeIcon icon={faLightbulb} /></div>
                    <div className="stat-info"><h4>{stats.useful}</h4><p>Խորհուրդներ</p></div>
                </div>
                <div className="stat-glass-card indigo">
                    <div className="stat-icon"><FontAwesomeIcon icon={faNewspaper} /></div>
                    <div className="stat-info"><h4>{stats.news}</h4><p>Լուրեր</p></div>
                </div>
                <div className="stat-glass-card emerald">
                    <div className="stat-icon"><FontAwesomeIcon icon={faTags} /></div>
                    <div className="stat-info"><h4>{stats.offers}</h4><p>Ակցիաներ</p></div>
                </div>
                <div className="stat-glass-card rose">
                    <div className="stat-icon"><FontAwesomeIcon icon={faBolt} /></div>
                    <div className="stat-info"><h4>{stats.interesting}</h4><p>Հետաքրքիր</p></div>
                </div>
            </div>

            <div className="main-grid">
                {/* Form Panel */}
                <div className="premium-panel">
                    <div className="panel-title">
                        <h3>{editId ? "Խմբագրել Հոդվածը" : "Ստեղծել Նոր Հոդված"}</h3>
                    </div>
                    
                    <form onSubmit={handleSubmit} className="premium-form">
                        <div className="input-group">
                            <label>Վերնագիր</label>
                            <input value={title} onChange={e => setTitle(e.target.value)} required placeholder="Մուտքագրեք վերնագիրը..." />
                        </div>

                        <div className="flex-row">
                            <div className="input-group flex-2">
                                <label>URL Slug</label>
                                <input value={slug} onChange={e => setSlug(e.target.value)} placeholder="օրինակ՝ transport-logistics..." />
                            </div>
                            <div className="input-group flex-1">
                                <label>Կատեգորիա</label>
                                <select value={category} onChange={e => setCategory(e.target.value)}>
                                    <option value="Օգտակար խորհուրդներ">Օգտակար խորհուրդներ</option>
                                    <option value="Նորություններ">Նորություններ</option>
                                    <option value="Հատուկ առաջարկներ">Հատուկ առաջարկներ</option>
                                    <option value="Հետաքրքիր">Հետաքրքիր</option>
                                </select>
                            </div>
                        </div>

                        <div className="luxury-upload">
                            <input type="file" id="file" onChange={e => setImage(e.target.files[0])} accept="image/*" />
                            <label htmlFor="file">
                                <FontAwesomeIcon icon={image ? faCheckCircle : faImage} className={image ? 'success' : ''} />
                                {image ? `Նկարը կցված է` : 'Կցել գլխավոր նկարը'}
                            </label>
                        </div>

                        <div className="quill-premium">
                            <ReactQuill theme="snow" value={content} onChange={setContent} />
                        </div>

                        <div className="btn-group">
                            <button type="submit" className="glow-btn" disabled={loading}>
                                {loading ? "Մշակվում է..." : (editId ? 'Թարմացնել' : 'Հրապարակել')}
                            </button>
                            {editId && <button onClick={resetForm} type="button" className="cancel-minimal">Չեղարկել</button>}
                        </div>
                    </form>
                </div>

                {/* List Panel */}
                <div className="premium-panel list-panel">
                    <div className="luxury-search" style={{display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '25px'}}>
                        <div style={{position: 'relative', flex: 1}}>
                            <FontAwesomeIcon icon={faSearch} style={{position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8'}} />
                            <input 
                                style={{width: '100%', padding: '12px 12px 12px 45px', borderRadius: '14px', border: '1px solid #e2e8f0', background: '#f8fafc', outline: 'none'}}
                                placeholder="Փնտրել..." 
                                value={searchTerm} 
                                onChange={e => setSearchTerm(e.target.value)} 
                            />
                        </div>
                        <select 
                            value={filterCategory} 
                            onChange={e => setFilterCategory(e.target.value)}
                            style={{padding: '12px', borderRadius: '14px', border: '1px solid #e2e8f0', background: '#f8fafc', fontWeight: '600', cursor: 'pointer'}}
                        >
                            <option>Բոլորը</option>
                            <option>Օգտակար խորհուրդներ</option>
                            <option>Նորություններ</option>
                            <option>Հատուկ առաջարկներ</option>
                            <option>Հետաքրքիր</option>
                        </select>
                    </div>

                    <div className="luxury-list">
                        {filteredBlogs.map(blog => (
                            <div key={blog.id} className="luxury-item">
                                <div className="item-meta">
                                    <h4>{blog.title}</h4>
                                    <span className={`pill ${
                                        blog.category === 'Նորություններ' ? 'indigo' : 
                                        blog.category === 'Հատուկ առաջարկներ' ? 'emerald' : 
                                        blog.category === 'Հետաքրքիր' ? 'rose' : 'gold'
                                    }`}>{blog.category}</span>
                                </div>
                                <div className="item-actions">
                                    <button onClick={() => {
                                        setEditId(blog.id); 
                                        setTitle(blog.title); 
                                        setSlug(blog.slug); 
                                        setCategory(blog.category); 
                                        setContent(blog.content);
                                        window.scrollTo({top: 0, behavior: 'smooth'});
                                    }} className="action-btn edit"><FontAwesomeIcon icon={faEdit}/></button>
                                    <button onClick={() => handleDelete(blog.id)} className="action-btn delete"><FontAwesomeIcon icon={faTrash}/></button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

const luxuryStyles = `
    @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700&display=swap');
    .admin-luxury-container { padding: 40px; background: #fcfdfe; min-height: 100vh; font-family: 'Plus Jakarta Sans', sans-serif; color: #1a1f36; }
    .premium-header { margin-bottom: 40px; }
    .header-brand { display: flex; align-items: center; gap: 20px; }
    .bolt-icon { width: 60px; height: 60px; background: linear-gradient(135deg, #6366f1 0%, #a855f7 100%); color: white; border-radius: 18px; display: flex; align-items: center; justify-content: center; font-size: 28px; box-shadow: 0 10px 20px rgba(99, 102, 241, 0.3); }
    .header-brand h1 { margin: 0; font-size: 26px; font-weight: 800; }
    .header-brand h1 span { color: #6366f1; font-weight: 400; font-size: 14px; margin-left: 5px; }
    .header-brand p { margin: 0; color: #64748b; font-size: 14px; }
    .analytics-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 20px; margin-bottom: 40px; }
    .stat-glass-card { background: white; padding: 24px; border-radius: 24px; display: flex; align-items: center; gap: 15px; box-shadow: 0 10px 15px -3px rgba(0,0,0,0.03); border: 1px solid rgba(255,255,255,0.8); transition: 0.3s; }
    .stat-glass-card:hover { transform: translateY(-5px); }
    .stat-icon { width: 48px; height: 48px; border-radius: 14px; display: flex; align-items: center; justify-content: center; font-size: 20px; }
    .blue .stat-icon { background: #e0e7ff; color: #4338ca; }
    .gold .stat-icon { background: #fef3c7; color: #b45309; }
    .indigo .stat-icon { background: #e0e7ff; color: #6366f1; }
    .emerald .stat-icon { background: #d1fae5; color: #059669; }
    .rose .stat-icon { background: #ffe4e6; color: #e11d48; }
    .stat-info h4 { margin: 0; font-size: 24px; font-weight: 800; }
    .stat-info p { margin: 0; font-size: 12px; color: #64748b; font-weight: 600; text-transform: uppercase; }
    .main-grid { display: grid; grid-template-columns: 1.5fr 1fr; gap: 30px; }
    .premium-panel { background: white; border-radius: 32px; padding: 35px; border: 1px solid #f1f5f9; box-shadow: 0 20px 50px rgba(0,0,0,0.03); }
    .premium-form { display: flex; flex-direction: column; gap: 20px; }
    .input-group label { display: block; font-size: 13px; font-weight: 700; color: #475569; margin-bottom: 8px; }
    .input-group input, .input-group select { width: 100%; padding: 16px 20px; border-radius: 16px; border: 1px solid #e2e8f0; background: #f8fafc; outline: none; transition: 0.3s; }
    .input-group input:focus { border-color: #6366f1; background: white; }
    .flex-row { display: flex; gap: 20px; }
    .flex-2 { flex: 2; } .flex-1 { flex: 1; }
    .luxury-upload label { display: flex; align-items: center; justify-content: center; gap: 10px; padding: 20px; border: 2px dashed #cbd5e1; border-radius: 16px; cursor: pointer; color: #64748b; font-weight: 600; transition: 0.3s; }
    .luxury-upload label:hover { border-color: #6366f1; background: #f1f5f9; }
    #file { display: none; }
    .quill-premium { margin-bottom: 50px; }
    .quill { border-radius: 16px; border: 1px solid #e2e8f0 !important; height: 300px; }
    .btn-group { display: flex; gap: 20px; }
    .glow-btn { flex: 1; background: #1a1f36; color: white; padding: 18px; border-radius: 16px; border: none; font-weight: 700; cursor: pointer; transition: 0.3s; }
    .glow-btn:hover { background: #2d3359; transform: translateY(-2px); }
    .luxury-item { padding: 20px; border-radius: 20px; border: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px; transition: 0.3s; }
    .luxury-item:hover { border-color: #6366f1; background: #f8fafc; }
    .pill { padding: 5px 12px; border-radius: 10px; font-size: 10px; font-weight: 800; }
    .pill.gold { background: #fef3c7; color: #b45309; }
    .pill.indigo { background: #e0e7ff; color: #4338ca; }
    .pill.emerald { background: #d1fae5; color: #065f46; }
    .pill.rose { background: #ffe4e6; color: #e11d48; }
    .action-btn { width: 38px; height: 38px; border-radius: 12px; border: none; background: white; box-shadow: 0 2px 5px rgba(0,0,0,0.05); margin-left: 8px; cursor: pointer; color: #64748b; transition: 0.2s; }
    .action-btn.edit:hover { background: #6366f1; color: white; }
    .action-btn.delete:hover { background: #ef4444; color: white; }
    /* --- MOBILE RESPONSIVE (Ավելացրու սա luxuryStyles-ի մեջ) --- */
    @media (max-width: 1100px) {
        .main-grid { grid-template-columns: 1fr; } /* Ֆորման ու Ցուցակը իրար տակ */
        .analytics-grid { grid-template-columns: repeat(3, 1fr); } /* 3 սյունակ պլանշետի վրա */
    }

    @media (max-width: 768px) {
        .admin-luxury-container { padding: 20px; }
        .analytics-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; } /* 2 սյունակ մոբայլում */
        .stat-glass-card { padding: 15px; border-radius: 18px; }
        .stat-info h4 { font-size: 18px; }
        
        .premium-panel { padding: 20px; border-radius: 20px; }
        .flex-row { flex-direction: column; gap: 0; }
        
        .header-brand h1 { font-size: 20px; }
        .header-brand .bolt-icon { width: 45px; height: 45px; font-size: 20px; }

        .luxury-item { flex-direction: column; align-items: flex-start; gap: 15px; }
        .item-actions { width: 100%; display: flex; justify-content: flex-end; }
        
        .quill { height: 250px; margin-bottom: 80px; } /* Էդիտորի բարձրությունը */
        .glow-btn { padding: 15px; font-size: 14px; }
    }

    @media (max-width: 480px) {
        .analytics-grid { grid-template-columns: 1fr; } /* Լրիվ մոբայլում 1 սյունակ */
        .stat-glass-card { justify-content: flex-start; }
    }
`;

export default AdminBlog;